import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { UserProfile, VerifyOtpSuccess } from "@/types/auth";
import type { SwitchedProfile } from "@/types/profile";

interface UserState {
  userProfile: UserProfile | null;
  activeProfile: SwitchedProfile | null;
  isLoggedIn: boolean;

  setUser: (profile: UserProfile) => void;
  setUserFromVerifyOtp: (data: VerifyOtpSuccess["data"]) => void;
  updateUserProfile: (profile: UserProfile) => void;
  setActiveProfile: (profile: SwitchedProfile) => void;
  clearUser: () => void;
}

export const useUserStore = create<UserState>()(
  persist(
    (set) => ({
      userProfile: null,
      activeProfile: null,
      isLoggedIn: false,

      // A new login must never keep a profile switched-to by whoever was
      // previously logged in on this device — otherwise their name/avatar
      // leaks into the new session (activeProfile is only reset on
      // clearUser(), which happens on explicit logout, not on login).
      setUser: (profile) =>
        set({ userProfile: profile, isLoggedIn: true, activeProfile: null }),

      setUserFromVerifyOtp: (data) =>
        set({
          userProfile: data.user_profile,
          isLoggedIn: true,
          activeProfile: null,
        }),

      // Used for session-continuation updates (e.g. token refresh) where the
      // logged-in user hasn't changed, so any switched-to profile must stay intact.
      updateUserProfile: (profile) => set({ userProfile: profile }),

      setActiveProfile: (profile) => set({ activeProfile: profile }),

      clearUser: () => set({ userProfile: null, activeProfile: null, isLoggedIn: false }),
    }),
    {
      name: "user-storage",
      storage: createJSONStorage(() => localStorage)
    }
  )
);